import React, { useEffect, useState, useRef } from "react";
import { Modal, Tag, Descriptions, message, Spin, Checkbox, Button } from "antd";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "./FacilityCalendarWidget.css";

const STATUS_COLORS = {
  "pending": "#fa8c16",       // orange
  "approved_unpaid": "#1890ff", // blue
  "approved_paid": "#52c41a",   // green
  "rejected": "#d9d9d9",       // gray
};

const getEventColor = (event) => {
  const status = (event.status || "").toLowerCase();
  if (status === "rejected" || status === "denied") return STATUS_COLORS.rejected;
  if (status === "pending") return STATUS_COLORS.pending;
  if (status === "approved") {
    return event.paid === 1 || event.paid === "1"
      ? STATUS_COLORS.approved_paid
      : STATUS_COLORS.approved_unpaid;
  }
  return STATUS_COLORS.pending;
};

const getEventTitle = (event) => {
  const facility = event.facility || "Facility";
  const applicant = event.applicant_name || "";
  const status = (event.status || "").toLowerCase();
  let label = "";
  if (status === "pending") label = "Pending";
  else if (status === "approved") label = event.paid === 1 || event.paid === "1" ? "Paid" : "Unpaid";
  else if (status === "rejected" || status === "denied") label = "Rejected";

  return `${facility} - ${applicant} (${label})`;
};

const FacilityCalendarWidget = ({ events, loading, onDateRangeChange, focusDate, onReserveClick }) => {
  const calendarRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [hideRejected, setHideRejected] = useState(true);

  const filteredEvents = (events || []).filter((evt) => {
    if (!hideRejected) return true;
    const status = (evt.status || "").toLowerCase();
    return status !== "rejected" && status !== "denied";
  });

  const calendarEvents = filteredEvents.map((evt) => {
    const startTime = evt.event_start_time || "00:00:00";
    const endTime = evt.event_end_time || "23:59:59";
    return {
      id: evt.id,
      title: getEventTitle(evt),
      start: `${evt.event_date}T${startTime}`,
      end: `${evt.event_date}T${endTime}`,
      backgroundColor: getEventColor(evt),
      borderColor: getEventColor(evt),
      extendedProps: evt,
    };
  });

  const handleEventClick = (info) => {
    setSelectedEvent(info.event.extendedProps);
    setDetailVisible(true);
  };

  const handleDatesSet = (dateInfo) => {
    if (onDateRangeChange) {
      const start = dateInfo.startStr.split("T")[0];
      const end = dateInfo.endStr.split("T")[0];
      onDateRangeChange(start, end);
    }
  };

  const handleDateClick = (info) => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.changeView("timeGridDay", info.dateStr);
    }
  };

  useEffect(() => {
    if (!focusDate) return;
    const api = calendarRef.current?.getApi();
    if (api) {
      api.changeView("timeGridDay", focusDate);
    }
  }, [focusDate]);

  const getStatusTag = (event) => {
    if (!event) return null;
    const status = (event.status || "").toLowerCase();
    if (status === "pending") return <Tag color="orange">Pending</Tag>;
    if (status === "rejected" || status === "denied") return <Tag color="red">Rejected</Tag>;
    if (status === "approved") {
      return event.paid === 1 || event.paid === "1"
        ? <Tag color="green">Paid</Tag>
        : <Tag color="blue">Approved - Unpaid</Tag>;
    }
    return <Tag>{status}</Tag>;
  };

  return (
    <div className="facility-calendar-container">
      <div className="facility-calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: STATUS_COLORS.pending }} />
          <span>Pending Approval</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: STATUS_COLORS.approved_unpaid }} />
          <span>Approved - Unpaid</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: STATUS_COLORS.approved_paid }} />
          <span>Approved - Paid</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: STATUS_COLORS.rejected }} />
          <span>Rejected</span>
        </div>
        <div className="legend-item" style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center" }}>
          <Checkbox checked={hideRejected} onChange={(e) => setHideRejected(e.target.checked)}>
            Hide Rejected
          </Checkbox>
          <Button type="primary" onClick={() => onReserveClick && onReserveClick()}>
            Reserve Facility
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          events={calendarEvents}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          dateClick={handleDateClick}
          height="auto"
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            meridiem: "short",
          }}
        />
      </Spin>

      <Modal
        title="Reservation Details"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={500}
      >
        {selectedEvent && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Facility">{selectedEvent.facility}</Descriptions.Item>
            <Descriptions.Item label="Applicant">{selectedEvent.applicant_name}</Descriptions.Item>
            <Descriptions.Item label="Event Date">{selectedEvent.event_date?.replaceAll("-", "/")}</Descriptions.Item>
            <Descriptions.Item label="Time">
              {selectedEvent.event_start_time} - {selectedEvent.event_end_time}
            </Descriptions.Item>
            <Descriptions.Item label="Hours">{selectedEvent.total_hours}</Descriptions.Item>
            <Descriptions.Item label="Amount">
              ${parseFloat(selectedEvent.total_amount || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Status">{getStatusTag(selectedEvent)}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedEvent.contact_email}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selectedEvent.contact_phone}</Descriptions.Item>
            {selectedEvent.event_type && (
              <Descriptions.Item label="Event Type">{selectedEvent.event_type}</Descriptions.Item>
            )}
            {selectedEvent.reject_reason && (
              <Descriptions.Item label="Reject Reason">{selectedEvent.reject_reason}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default FacilityCalendarWidget;
